/**
 * Chorus Pay - App Block
 * Handles the "Devis Chorus Pro" button click: fetches the cart,
 * sends it to the Chorus Pay API, and redirects to the pay link.
 * The button HTML is rendered by the Liquid App Block.
 */

class ChorusPay extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
    this.apiUrl = this.dataset.apiUrl;
    this.shopUrl = this.dataset.shopUrl;
    this.taxRate = parseFloat(this.dataset.taxRate || "20");
    this.originalText = this.dataset.buttonText || "Devis Chorus Pro";

    if (this.button) {
      this.button.addEventListener("click", this.handlePayment.bind(this));
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) this.resetButton();
      });
    }
  }

  get labelEl() {
    return this.button.querySelector(".chorus-pay-label");
  }

  resetButton() {
    this.button.disabled = false;
    this.button.classList.remove("loading");
    if (this.labelEl) {
      this.labelEl.textContent = this.originalText;
    }
  }

  async handlePayment(event) {
    event.preventDefault();
    this.button.disabled = true;
    this.button.classList.add("loading");
    if (this.labelEl) {
      this.labelEl.textContent = "Chargement...";
    }

    try {
      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok) throw new Error("Impossible de récupérer le panier");
      const cartData = await cartResponse.json();

      if (!cartData.items || cartData.items.length === 0) {
        throw new Error("Le panier est vide");
      }

      const vatRateStr = String(this.taxRate);
      const lines = cartData.items.map((item) => {
        const unitPriceTtc = item.price / 100;
        const unitPriceHt = unitPriceTtc / (1 + this.taxRate / 100);
        return {
          description: item.title || item.product_title,
          quantity: item.quantity,
          unit_price: Math.round(unitPriceHt * 100) / 100,
          vat_rate: vatRateStr,
          reference: item.sku || String(item.variant_id),
          variant_id: String(item.variant_id),
        };
      });

      const payload = {
        shop_url: this.shopUrl,
        cart_token: cartData.token,
        lines,
        description: "Commande Shopify",
      };

      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error?.message || "Erreur lors de la communication avec Chorus Pay");
      }

      const result = await response.json();
      if (result.pay_link_url) {
        window.location.href = result.pay_link_url;
      } else {
        throw new Error("Aucun lien de paiement reçu");
      }
    } catch (error) {
      console.error("Erreur Chorus Pay:", error);
      this.showError(error.message || "Une erreur est survenue. Veuillez réessayer.");
      this.resetButton();
    }
  }

  showError(message) {
    const existing = this.querySelector(".chorus-pay-error");
    if (existing) existing.remove();
    const errorDiv = document.createElement("div");
    errorDiv.className = "chorus-pay-error";
    errorDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <span>${message}</span>
    `;
    this.appendChild(errorDiv);
    setTimeout(() => errorDiv.remove(), 5000);
  }
}

if (!customElements.get("chorus-pay")) {
  customElements.define("chorus-pay", ChorusPay);
}
